import camelcaseKeys from 'camelcase-keys';
import { z } from 'zod';

import { apiResponseError } from './factories';
import { HTTP_STATUS } from '../consts';

/*
 * Error bodies that predate the Manage API error envelope (Tools ADR-0020). Each one is checked
 * only for its own status. Known senders, by the app code that reads them:
 * - 400 `{ errors }`: auth (login, sign-up, forgot / reset password, verify email), location create and LSG report update.
 * - 401 `{ message, errors? }`: auth login and token refresh.
 * - 401 `{ errors: { code } }` with no `message`: auth 2FA verify (`WRONG_2FA_CODE`, `INVALID_2FA_CHALLENGE`).
 *   It matches no schema here, so it arrives as an unknown error; the login reads it with `TwoFactorErrorBodySchema`.
 * - 403 `{ code }`: forgot password (`DEMO_ACCOUNT`).
 * - 404 `{ message }`: the insights status endpoint.
 * - 429 `{ code: 'RATE_LIMITED' }`: auth mocks only; no live endpoint is known to send it.
 * - 429 `{ errors: { code, retry_after } }`: auth login and 2FA verify (`TWO_FACTOR_ATTEMPTS_EXCEEDED`).
 *   Read the same way as the 2FA 401 above.
 * - 500 `{ error: { code: number } }`: the internal-error body.
 * When an endpoint moves to the envelope, delete its entry here.
 */

// Define a recursive type for nested error objects.
// Values can be: a single string, an array of strings, or a nested record.
const ErrorValueSchema: z.ZodType<string | string[] | Record<string, unknown>> = z.lazy(() =>
  z.union([z.string(), z.array(z.string()), z.record(z.union([z.string(), z.array(z.string()), ErrorValueSchema]))])
);

// 400 Bad Request - validation errors (keys are transformed from snake_case to camelCase)
export const BadRequestErrorSchema = z
  .object({
    errors: z.record(ErrorValueSchema)
  })
  .transform((data) => ({
    errors: camelcaseKeys(data.errors, { deep: true })
  }));

export type BadRequestError = z.infer<typeof BadRequestErrorSchema>;

// 401 Unauthorized. Auth endpoints add an `errors.code` wrapper (e.g. `PASSWORD_RESET_REQUIRED`)
// that consumers branch on, so it is kept when present.
export const UnauthorizedErrorSchema = z.object({
  message: z.string(),
  errors: z.object({ code: z.string() }).optional()
});

export type UnauthorizedError = z.infer<typeof UnauthorizedErrorSchema>;

// 403 Forbidden
export const ForbiddenErrorSchema = z.object({
  code: z.string()
});

export type ForbiddenError = z.infer<typeof ForbiddenErrorSchema>;

// 404 Not Found
export const NotFoundErrorSchema = z.object({
  message: z.string(),
  resourceId: z.string().optional()
});

export type NotFoundError = z.infer<typeof NotFoundErrorSchema>;

// 429 Too Many Requests
export const RateLimitedErrorSchema = z
  .object({
    code: z.literal('RATE_LIMITED'),
    retry_after: z.number().optional()
  })
  .transform((data) => camelcaseKeys(data));

export type RateLimitedError = z.infer<typeof RateLimitedErrorSchema>;

// 500 Internal Server Error
export const InternalServerErrorSchema = z.object({
  error: z.object({
    code: z.number(),
    message: z.string(),
    details: z.string()
  })
});

/** Maps a body in one of the pre-envelope shapes to an `ApiResponseError`, or `undefined` when none matches. */
export const handleLegacyError = (status: number, error: unknown) => {
  if (status === HTTP_STATUS.VALIDATION_ERROR) {
    const result = BadRequestErrorSchema.safeParse(error);

    if (result.success) {
      return apiResponseError('Bad request', status, result.data.errors);
    }
  }

  if (status === HTTP_STATUS.AUTHORIZATION_ERROR) {
    const result = UnauthorizedErrorSchema.safeParse(error);

    if (result.success) {
      return apiResponseError(result.data.message, status, result.data);
    }
  }

  if (status === HTTP_STATUS.FORBIDDEN) {
    const result = ForbiddenErrorSchema.safeParse(error);

    if (result.success) {
      return apiResponseError('Forbidden', status, result.data);
    }
  }

  if (status === HTTP_STATUS.NOT_FOUND) {
    const result = NotFoundErrorSchema.safeParse(error);

    if (result.success) {
      return apiResponseError(result.data.message, status, result.data);
    }
  }

  if (status === HTTP_STATUS.TOO_MANY_REQUESTS) {
    const result = RateLimitedErrorSchema.safeParse(error);

    if (result.success) {
      return apiResponseError('Too many requests', status, result.data);
    }
  }

  if (status === HTTP_STATUS.INTERNAL_SERVER_ERROR) {
    const result = InternalServerErrorSchema.safeParse(error);

    if (result.success) {
      return apiResponseError(result.data.error.message, status, result.data);
    }
  }

  return undefined;
};
